import { useTranslation } from 'react-i18next';
import { ChatContextType, ShotsData, ShotsAIResponse, ScriptShot } from '@packages/global';
import BaseStepChatPanel, { ActionCard } from './BaseStepChatPanel';

export interface ShotsChatPanelProps {
  projectId: string;
  episodeScriptId: string;
  currentData?: ShotsData | null;
  onApply?: (action: { type: 'create' | 'update' | 'delete' | 'reorder'; shot?: ScriptShot; index?: number; toIndex?: number }) => void;
}

function stripCodeFence(content: string): string {
  return content
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();
}

function decodeJsonStringFragment(fragment: string): string {
  try {
    return JSON.parse(`"${fragment.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
  } catch {
    return fragment;
  }
}

function extractMessageFallback(content: string): string {
  const messageMatch = content.match(/"message"\s*:\s*"((?:\\.|[^"\\])*)"/s);
  if (messageMatch?.[1]) {
    return decodeJsonStringFragment(messageMatch[1]);
  }
  return content;
}

function extractCompleteActionObjects(content: string): any[] {
  const actionsKeyIndex = content.indexOf('"actions"');
  if (actionsKeyIndex < 0) return [];

  const arrayStart = content.indexOf('[', actionsKeyIndex);
  if (arrayStart < 0) return [];

  const actions: any[] = [];
  let depth = 0;
  let objectStart = -1;
  let inString = false;
  let escaped = false;

  for (let i = arrayStart + 1; i < content.length; i += 1) {
    const ch = content[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === '{') {
      if (depth === 0) {
        objectStart = i;
      }
      depth += 1;
      continue;
    }

    if (ch === '}') {
      depth -= 1;
      if (depth === 0 && objectStart !== -1) {
        const rawObject = content.slice(objectStart, i + 1);
        try {
          actions.push(JSON.parse(rawObject));
        } catch {
          // 忽略截断或畸形对象，继续提取后续完整对象
        }
        objectStart = -1;
      }
      continue;
    }

    if (ch === ']' && depth === 0) {
      break;
    }
  }

  return actions;
}

export default function ShotsChatPanel({ projectId, episodeScriptId, currentData, onApply }: ShotsChatPanelProps) {
  const { t } = useTranslation('script');
  const parseResponse = (content: string): { message: string; actions: ActionCard[] } => {
    try {
      let parsed: ShotsAIResponse | null = null;
      const normalized = stripCodeFence(content);

      // 1. 尝试匹配 markdown 代码块中的 JSON
      const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        try {
          parsed = JSON.parse(jsonMatch[1]);
        } catch {
          // 继续尝试宽松解析
        }
      }

      // 2. 尝试直接解析为 JSON
      if (!parsed) {
        const trimmed = normalized;
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
          try {
            parsed = JSON.parse(trimmed);
          } catch {
            // 继续尝试宽松解析
          }
        }
      }

      // 3. 尝试提取 JSON 对象
      if (!parsed) {
        const jsonObjectMatch = normalized.match(/\{[\s\S]*"message"[\s\S]*\}/);
        if (jsonObjectMatch) {
          try {
            parsed = JSON.parse(jsonObjectMatch[0]);
          } catch {
            // 继续尝试宽松解析
          }
        }
      }

      if (!parsed) {
        const message = extractMessageFallback(normalized);
        const fallbackActions = extractCompleteActionObjects(normalized).map(a => {
          let type: 'update' | 'add' | 'delete' = 'update';
          if (a.actionType === 'create') type = 'add';
          else if (a.actionType === 'delete') type = 'delete';
          else if (a.actionType === 'update' || a.actionType === 'reorder') type = 'update';

          const shotNumber = (a.shotIndex !== undefined ? a.shotIndex : (currentData?.shots?.length || 0)) + 1;

          return {
            type,
            title: t('shots.shotLabel', { number: shotNumber }),
            content: a.shot?.description || t('shots.shotLabel', { number: shotNumber }),
            data: a
          };
        });

        if (message !== normalized || fallbackActions.length > 0) {
          return { message, actions: fallbackActions };
        }

        return { message: content, actions: [] };
      }

      const actions: ActionCard[] = (parsed.actions || []).map(a => {
        // 映射 actionType 到 ActionCard.type
        let type: 'update' | 'add' | 'delete' = 'update';
        if (a.actionType === 'create') type = 'add';
        else if (a.actionType === 'delete') type = 'delete';
        else if (a.actionType === 'update' || a.actionType === 'reorder') type = 'update';

        // 分镜编号（从 1 开始显示）
        const shotNumber = (a.shotIndex !== undefined ? a.shotIndex : (currentData?.shots?.length || 0)) + 1;

        return {
          type,
          title: t('shots.shotLabel', { number: shotNumber }),
          content: a.shot?.description || t('shots.shotLabel', { number: shotNumber }),
          data: a
        };
      });
      return { message: parsed.message || '', actions };
    } catch {
      return { message: content, actions: [] };
    }
  };

  const handleApplyAction = (action: ActionCard) => {
    if (!onApply || !action.data) return;
    const data = action.data as { actionType: string; shotIndex?: number; shot?: ScriptShot };
    if (data.actionType === 'create' && data.shot) {
      // 如果指定了 shotIndex，则在该位置插入；否则添加到末尾
      onApply({ type: 'create', shot: data.shot, index: data.shotIndex });
    } else if (data.actionType === 'update' && data.shot && data.shotIndex !== undefined) {
      onApply({ type: 'update', shot: data.shot, index: data.shotIndex });
    } else if (data.actionType === 'delete' && data.shotIndex !== undefined) {
      onApply({ type: 'delete', index: data.shotIndex });
    }
  };

  return (
    <BaseStepChatPanel
      projectId={projectId}
      episodeScriptId={episodeScriptId}
      stepNumber={6}
      stepName={t('step.shots')}
      contextType={ChatContextType.SCRIPT_SHOTS}
      currentStepData={currentData}
      onApplyAction={handleApplyAction}
      parseResponse={parseResponse}
    />
  );
}
